import React, { useState, useEffect } from 'react';
import axios from 'axios';

const SchoolTiming = () => {
  const [assemblyTime, setAssemblyTime] = useState("");
  const [assemblyDuration, setAssemblyDuration] = useState(0);
  const [periodDuration, setPeriodDuration] = useState(0);
  const [periodNumber, setPeriodNumber] = useState(0);
  const [lunchBreakDuration, setLunchBreakDuration] = useState(0);
  const [lunchBreakAfterPeriod, setLunchBreakAfterPeriod] = useState(1);
  const [scheduleName, setScheduleName] = useState("");
  const [schoolTimings, setSchoolTimings] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  const labelStyle = "block text-black font-semibold text-[18px] mb-2";
  const inputStyle =
    "block w-full p-2 border border-gray-300 rounded text-[14px] " +
    "placeholder:text-[14px] placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500";

  // Get sessionId from localStorage
  useEffect(() => {
    const storedSessionId = localStorage.getItem("currentSessionId");
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }
  }, []);

  // Fetch school timings for current session
  useEffect(() => {
    if (!sessionId) return;

    axios
      .get(` ${process.env.REACT_APP_API_BASE_URL}/api/schooltiming/getAllSchoolTimings?sessionId=${sessionId}`)
      .then((res) => setSchoolTimings(res.data))
      .catch((err) => console.error("Error fetching school timings:", err));
  }, [sessionId]);

  const handleSaveSchoolTiming = () => {
    if (
      !assemblyTime ||
      !assemblyDuration ||
      !periodDuration ||
      !periodNumber ||
      !lunchBreakDuration ||
      !scheduleName
    ) {
      alert("Please fill all the fields.");
      return;
    }

    const schoolTimingData = {
      assemblyTime,
      assemblyDuration,
      periodDuration,
      periodNumber,
      lunchBreakDuration,
      lunchBreakAfterPeriod,
      scheduleName,
      sessionId // include sessionId in payload
    };

    if (editMode && currentId) {
      axios
        .put(
          ` ${process.env.REACT_APP_API_BASE_URL}/api/schooltiming/updateSchoolTiming/${currentId}`,
          schoolTimingData
        )
        .then((res) => {
          alert("School timing updated successfully!");
          setSchoolTimings((prev) =>
            prev.map((timing) => (timing._id === currentId ? res.data : timing))
          );
          clearForm();
        })
        .catch(() => {
          alert("Error updating school timing. Please try again.");
        });
    } else {
      axios
        .post(
          `${process.env.REACT_APP_API_BASE_URL}/api/schooltiming/createSchoolTiming`,
          schoolTimingData
        )
        .then((res) => {
          alert("School timing saved successfully!");
          setSchoolTimings((prev) => [...prev, res.data]);
          clearForm();
        })
        .catch(() => alert("Error saving school timing. Please try again."));
    }
  };

  const handleUpdateSchoolTiming = (id) => {
    const timing = schoolTimings.find((t) => t._id === id);
    if (timing) {
      setAssemblyTime(timing.assemblyTime);
      setAssemblyDuration(timing.assemblyDuration);
      setPeriodDuration(timing.periodDuration);
      setPeriodNumber(timing.periodNumber);
      setLunchBreakDuration(timing.lunchBreakDuration);
      setLunchBreakAfterPeriod(timing.lunchBreakAfterPeriod || 1);
      setScheduleName(timing.scheduleName);
      setEditMode(true);
      setCurrentId(id);
    }
  };

  const handleDeleteSchoolTiming = (id) => {
    if (window.confirm("Are you sure you want to delete this school timing?")) {
      axios
        .delete(` ${process.env.REACT_APP_API_BASE_URL}/api/schooltiming/deleteSchoolTiming/${id}?sessionId=${sessionId}`)
        .then(() => {
          alert("School timing deleted successfully!");
          setSchoolTimings((prev) => prev.filter((t) => t._id !== id));
        })
        .catch(() => alert("Error deleting school timing."));
    }
  };

  const clearForm = () => {
    setAssemblyTime("");
    setAssemblyDuration(0);
    setPeriodDuration(0);
    setPeriodNumber(0);
    setLunchBreakDuration(0);
    setLunchBreakAfterPeriod(1);
    setScheduleName("");
    setEditMode(false);
    setCurrentId(null);
  };

  return (
    <div className="p-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className={labelStyle}>Schedule Name:</label>
          <input
            type="text"
            value={scheduleName}
            onChange={(e) => setScheduleName(e.target.value)}
            className={inputStyle}
            placeholder="Enter schedule name"
          />
        </div>

        <div>
          <label className={labelStyle}>Assembly Time:</label>
          <input
            type="time"
            value={assemblyTime}
            onChange={(e) => setAssemblyTime(e.target.value)}
            className={inputStyle}
          />
        </div>

        <div>
          <label className={labelStyle}>Assembly Duration (mins):</label>
          <input
            type="number"
            value={assemblyDuration}
            onChange={(e) => setAssemblyDuration(Number(e.target.value))}
            className={inputStyle}
            placeholder="Enter minutes"
          />
        </div>

        <div>
          <label className={labelStyle}>Period Duration (mins):</label>
          <input
            type="number"
            value={periodDuration}
            onChange={(e) => setPeriodDuration(Number(e.target.value))}
            className={inputStyle}
            placeholder="Enter minutes"
          />
        </div>

        <div>
          <label className={labelStyle}>Number of Periods:</label>
          <select
            value={periodNumber}
            onChange={(e) => setPeriodNumber(Number(e.target.value))}
            className={inputStyle}
          >
            <option value="">Select Number of Periods</option>
            {[...Array(10).keys()].map((num) => (
              <option key={num + 1} value={num + 1}>
                {num + 1}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelStyle}>Lunch Break Duration (mins):</label>
          <input
            type="number"
            value={lunchBreakDuration}
            onChange={(e) => setLunchBreakDuration(Number(e.target.value))}
            className={inputStyle}
            placeholder="Enter minutes"
          />
        </div>

        <div>
          <label className={labelStyle}>Lunch Break After Period:</label>
          <select
            value={lunchBreakAfterPeriod}
            onChange={(e) => setLunchBreakAfterPeriod(Number(e.target.value))}
            className={inputStyle}
          >
            <option value="">Select Period</option>
            {[...Array(periodNumber).keys()].map((num) => (
              <option key={num + 1} value={num + 1}>
                {num + 1}
              </option>
            ))}
          </select>
        </div>
      </div>

      <button
        onClick={handleSaveSchoolTiming}
        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded"
      >
        {editMode ? "Update School Timing" : "Save School Timing"}
      </button>

      {editMode && (
        <button
          onClick={clearForm}
          className="bg-gray-600 text-white py-2 px-4 ml-4 rounded"
        >
          Cancel
        </button>
      )}

      <div className="mt-8">
        <h2 className="font-bold text-[18px] text-black mb-4">
          Existing School Timings
        </h2>

        <table className="w-full bg-white shadow-md rounded text-center">
          <thead className="bg-gray-200">
            <tr>
              <th className="border p-4 text-[18px] font-semibold text-black">Sr No.</th>
              <th className="border p-4 text-[18px] font-semibold text-black">Schedule Name</th>
              <th className="border p-4 text-[18px] font-semibold text-black">Assembly Time</th>
              <th className="border p-4 text-[18px] font-semibold text-black">Actions</th>
            </tr>
          </thead>

          <tbody>
            {schoolTimings.map((timing, index) => (
              <tr key={timing._id} className="hover:bg-gray-100">
                <td className="border p-4">{index + 1}</td>
                <td className="border p-4">{timing.scheduleName}</td>
                <td className="border p-4">{timing.assemblyTime}</td>
                <td className="border p-4">
                  <div className="flex justify-center gap-3">
                    <button
                      onClick={() => handleUpdateSchoolTiming(timing._id)}
                      className="bg-yellow-500 hover:bg-yellow-400 text-white px-3 py-1 rounded"
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => handleDeleteSchoolTiming(timing._id)}
                      className="bg-red-600 hover:bg-red-500 text-white px-3 py-1 rounded"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {schoolTimings.length === 0 && (
              <tr>
                <td colSpan="4" className="p-4 text-gray-500">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SchoolTiming;
